// Các mảnh vẽ của Bộ soi ảnh: lớp bậc ảnh, nhãn khung, lưới căn, thanh gạt, kính lúp, bản đồ nhỏ.
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { LadderImage } from "./useLadderImage";
import type { Pan, Size } from "./viewport";

export const LOUPE_R = 96;

/** Các bậc ảnh của một thang: bậc đang hiện + bậc đang tải ngầm, khoá theo LINK để khỏi nháy. */
export function LadderImages({
  li,
  alt,
  onMeasure,
  pixelated,
}: {
  li: LadderImage;
  alt: string;
  onMeasure: (img: HTMLImageElement) => void;
  pixelated: boolean;
}) {
  const items = [li.shown, li.pending].filter((r): r is NonNullable<typeof r> => !!r);
  return (
    <>
      {items.map((r) => {
        const isShown = r.url === li.shown?.url;
        return (
          <img
            key={r.url}
            src={r.url}
            alt={isShown ? alt : ""}
            aria-hidden={isShown ? undefined : true}
            draggable={false}
            decoding="async"
            data-rung={r.label}
            onLoad={(e) => {
              onMeasure(e.currentTarget);
              if (isShown) li.onShownLoad();
              else li.onPendingLoad();
            }}
            onError={isShown ? li.onShownError : li.onPendingError}
            className={cn("absolute inset-0 h-full w-full select-none", !isShown && "opacity-0")}
            style={pixelated ? { imageRendering: "pixelated" } : undefined}
          />
        );
      })}
    </>
  );
}

export function PaneLabel({ children, right }: { children: ReactNode; right?: boolean }) {
  return (
    <span
      className={cn(
        "pointer-events-none absolute top-2 z-10 max-w-[45%] truncate rounded bg-black/60 px-2 py-0.5 text-xs font-medium text-white",
        right ? "right-2" : "left-2",
      )}
    >
      {children}
    </span>
  );
}

export function GridOverlay() {
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 300 300" preserveAspectRatio="none" aria-hidden>
      <g stroke="rgba(255,255,255,0.75)" strokeWidth="0.8" vectorEffect="non-scaling-stroke">
        <line x1="100" y1="0" x2="100" y2="300" />
        <line x1="200" y1="0" x2="200" y2="300" />
        <line x1="0" y1="100" x2="300" y2="100" />
        <line x1="0" y1="200" x2="300" y2="200" />
      </g>
      <g stroke="rgba(255,214,90,0.85)" strokeWidth="0.8" strokeDasharray="4 4" vectorEffect="non-scaling-stroke">
        <line x1="150" y1="0" x2="150" y2="300" />
        <line x1="0" y1="150" x2="300" y2="150" />
      </g>
    </svg>
  );
}

export function WipeHandle({ pos, onPos }: { pos: number; onPos: (p: number) => void }) {
  const set = (e: React.PointerEvent) => {
    const parent = (e.currentTarget as HTMLElement).parentElement;
    if (!parent) return;
    const r = parent.getBoundingClientRect();
    if (r.width > 0) onPos(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)));
  };
  return (
    <div
      role="slider"
      aria-label="Thanh gạt trước / sau"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos * 100)}
      tabIndex={0}
      className="absolute inset-y-0 z-10 flex w-11 -translate-x-1/2 cursor-ew-resize touch-none items-center justify-center"
      style={{ left: `${pos * 100}%` }}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        e.stopPropagation();
        if (e.buttons || e.pointerType !== "mouse") set(e);
      }}
      onPointerUp={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") onPos(Math.max(0, pos - 0.05));
        else if (e.key === "ArrowRight") onPos(Math.min(1, pos + 0.05));
        else return;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <span className="h-full w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]" aria-hidden />
      <span className="absolute grid h-9 w-9 place-items-center rounded-full bg-white text-sm font-bold text-black shadow" aria-hidden>
        ⇆
      </span>
    </div>
  );
}

export function Loupe({
  url,
  point,
  at,
  size,
  magnify,
}: {
  url: string;
  point: { x: number; y: number };
  at: { x: number; y: number };
  size: Size;
  magnify: number;
}) {
  const w = size.w * magnify;
  const h = size.h * magnify;
  return (
    <div
      data-testid="pv-loupe"
      aria-hidden
      className="pointer-events-none absolute z-20 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.45)]"
      style={{
        left: at.x - LOUPE_R,
        top: at.y - LOUPE_R,
        width: LOUPE_R * 2,
        height: LOUPE_R * 2,
        backgroundImage: `url("${url}")`,
        backgroundRepeat: "no-repeat",
        backgroundSize: `${w}px ${h}px`,
        backgroundPosition: `${LOUPE_R - point.x * w}px ${LOUPE_R - point.y * h}px`,
        backgroundColor: "#222",
      }}
    />
  );
}

export function MiniMap({
  url,
  size,
  rect,
  onPan,
}: {
  url: string;
  size: Size;
  rect: { x: number; y: number; w: number; h: number };
  onPan: (p: Pan) => void;
}) {
  const MAX = 116;
  const k = MAX / Math.max(size.w, size.h);
  const w = Math.round(size.w * k);
  const h = Math.round(size.h * k);
  const jump = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    onPan({
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    });
  };
  return (
    <div
      role="img"
      aria-label="Bản đồ nhỏ — chỗ đang soi trên ảnh"
      data-testid="pv-minimap"
      className="absolute bottom-14 right-2 z-10 cursor-pointer touch-none overflow-hidden rounded border border-white/70 bg-black/60 shadow"
      style={{ width: w, height: h }}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        jump(e);
      }}
      onPointerMove={(e) => {
        e.stopPropagation();
        if (e.buttons || e.pointerType !== "mouse") jump(e);
      }}
      onPointerUp={(e) => e.stopPropagation()}
    >
      <img src={url} alt="" className="h-full w-full object-fill opacity-80" draggable={false} />
      <span
        className="absolute border-2 border-[var(--gn-gold,#f5c542)] bg-white/10"
        style={{ left: rect.x * w, top: rect.y * h, width: rect.w * w, height: rect.h * h }}
      />
    </div>
  );
}
